import React, { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import FormFieldError from './FormFieldError';
import FormFieldLabel from './FormFieldLabel';
import FormFieldTooltip, { TooltipSource } from './FormFieldTooltip';
import { FormProvider } from './FormProvider';
import useFormField, { FieldEvent, FieldWidth } from './useFormField';

interface IProps {
  name: string;
  label?: string;
  tooltip?: TooltipSource;
  description?: string;
  placeholder?: number | string;
  min?: number;
  max?: number;
  disabled?: boolean;
  width?: FieldWidth;
  onFocus?: FieldEvent<number | string>;
  onChange?: FieldEvent<number | string>;
  onBlur?: FieldEvent<number | string>;
  currency?: '$' | '€' | '£';
}

function FormAmountField({
  name,
  label,
  tooltip,
  description,
  placeholder,
  min,
  max,
  disabled,
  width,
  onFocus,
  onChange,
  onBlur,
  currency = '$',
}: IProps) {
  const inputRef = useRef(null);
  const formatterRef = useRef(
    new Intl.NumberFormat('en', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: false,
    }),
  );

  const { locale } = useContext(FormProvider);
  const {
    id,
    fieldProps,
    inputProps,
    setFieldValue,
    value,
    isFocused,
    required,
    disabled: formDisabled,
  } = useFormField<number | string>(name, width, { onFocus, onChange, onBlur });

  const [valueFormatted, setValueFormatted] = useState(
    value !== null && isFinite(+value) ? formatterRef.current.format(+value) : '',
  );

  useEffect(() => {
    const onWheel = (e: WheelEvent) => e.preventDefault();
    const onKeyDown = (e: KeyboardEvent) => {
      if (['ArrowUp', 'ArrowDown'].includes(e.code)) {
        e.preventDefault();
      }
    };

    inputRef.current.addEventListener('wheel', onWheel);
    inputRef.current.addEventListener('keydown', onKeyDown);
    return () => {
      if (inputRef.current) {
        inputRef.current.removeEventListener('mousewheel', onWheel);
        inputRef.current.removeEventListener('keydown', onKeyDown);
      }
    };
  }, []);

  useEffect(() => {
    if (!isFocused && isFinite(+value)) {
      const valueFormatted = formatterRef.current.format(+value);
      setValueFormatted(valueFormatted);
    }
  }, [value, isFocused]);

  const getNumericValue = (value: string): number =>
    isFinite(+value) ? +formatterRef.current.format(+value) : null;

  const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const value = getNumericValue(e.target.value);

    setFieldValue(name, value);
    setValueFormatted(e.target.value);

    onChange && onChange(getNumericValue(e.target.value));
  };

  const isDisabled = formDisabled || disabled;
  const isInverted = locale === 'en';
  const rawValue = `${isInverted ? currency : ''}${valueFormatted}${!isInverted ? ` ${currency}` : ''}`;

  const inputClassName = classNames('novoInputWithSuffix', {
    'novoInputWithSuffix--disabled': isDisabled,
    'novoInputWithSuffix--inverted': isInverted,
  });
  return (
    <div {...fieldProps}>
      {label && (
        <div className="novoFormField__name">
          <FormFieldLabel for={id} required={required}>
            {label}
          </FormFieldLabel>
          <FormFieldTooltip source={tooltip} />
        </div>
      )}
      {description && <p className="novoFormField__description">{description}</p>}
      <div className={inputClassName} tabIndex={0} onFocus={() => inputRef?.current.focus()}>
        <input
          className="novoFormField__input"
          id={id}
          type="number"
          step="0.01"
          min={min}
          max={max}
          tabIndex={-1}
          autoComplete="off"
          ref={inputRef}
          {...inputProps}
          value={valueFormatted}
          onChange={onChangeHandler}
          lang={locale}
          required={required}
          disabled={isDisabled}
          placeholder={
            isFinite(placeholder as number) ? formatterRef.current.format(placeholder as number) : placeholder
          }
        />
        <span className="novoInputWithSuffix__suffix novoInputWithSuffix__suffix--padded">{currency}</span>
        <span className="novoFormField__raw" aria-hidden="true">
          {rawValue}
        </span>
      </div>
      <FormFieldError name={name} />
    </div>
  );
}

export default FormAmountField;
